import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../services/api';

const EditQuestionnaire = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [questionnaire, setQuestionnaire] = useState({
    title: '',
    description: '',
    questions: []
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(null);

  // Fetch questionnaire data
  useEffect(() => {
    const fetchQuestionnaire = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/questionnaires/${id}`);
        setQuestionnaire(response.data);
        
        // Set the first question as active if there are questions
        if (response.data.questions && response.data.questions.length > 0) {
          setActiveQuestionIndex(0);
        }
        
        setError(null);
      } catch (err) {
        setError('Failed to fetch questionnaire. Please try again later.');
        console.error('Error fetching questionnaire:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestionnaire();
  }, [id]);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setQuestionnaire(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error when field is edited
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Handle question text change
  const handleQuestionChange = (index, e) => {
    const { value } = e.target;
    const updatedQuestions = [...questionnaire.questions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      text: value
    };
    
    setQuestionnaire(prev => ({
      ...prev,
      questions: updatedQuestions
    }));
    
    // Clear validation error
    if (validationErrors[`questions[${index}].text`]) {
      setValidationErrors(prev => ({
        ...prev,
        [`questions[${index}].text`]: null
      }));
    }
  };

  // Handle option text change
  const handleOptionChange = (questionIndex, optionIndex, e) => {
    const { value } = e.target;
    const updatedQuestions = [...questionnaire.questions];
    updatedQuestions[questionIndex].options[optionIndex] = {
      ...updatedQuestions[questionIndex].options[optionIndex],
      text: value
    };
    
    setQuestionnaire(prev => ({
      ...prev,
      questions: updatedQuestions
    }));
  };

  // Handle option correctness toggle
  const handleCorrectOptionChange = (questionIndex, optionIndex) => {
    const updatedQuestions = [...questionnaire.questions];
    
    // Update all options to be incorrect first
    updatedQuestions[questionIndex].options.forEach((option, idx) => {
      option.isCorrect = idx === optionIndex;
    });
    
    setQuestionnaire(prev => ({
      ...prev,
      questions: updatedQuestions
    }));
  };

  // Add new question
  const addQuestion = () => {
    const newQuestions = [
      ...questionnaire.questions,
      {
        text: '',
        options: [
          { text: '', isCorrect: true },
          { text: '', isCorrect: false }
        ]
      }
    ];
    
    setQuestionnaire(prev => ({
      ...prev,
      questions: newQuestions
    }));
    
    // Set the new question as active
    setActiveQuestionIndex(newQuestions.length - 1);
    
    // Scroll to the new question after a short delay
    setTimeout(() => {
      const newQuestionElement = document.getElementById(`question-${newQuestions.length - 1}`);
      if (newQuestionElement) {
        newQuestionElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  // Remove question
  const removeQuestion = (index) => {
    const updatedQuestions = [...questionnaire.questions];
    updatedQuestions.splice(index, 1);
    
    setQuestionnaire(prev => ({
      ...prev,
      questions: updatedQuestions
    }));
    
    // Update active question index if needed
    if (activeQuestionIndex === index) {
      if (updatedQuestions.length > 0) {
        setActiveQuestionIndex(Math.min(index, updatedQuestions.length - 1));
      } else {
        setActiveQuestionIndex(null);
      }
    } else if (activeQuestionIndex > index) {
      setActiveQuestionIndex(activeQuestionIndex - 1);
    }
  };

  // Add option to question
  const addOption = (questionIndex) => {
    const updatedQuestions = [...questionnaire.questions];
    updatedQuestions[questionIndex].options.push({
      text: '',
      isCorrect: false
    });
    
    setQuestionnaire(prev => ({
      ...prev,
      questions: updatedQuestions
    }));
  };

  // Remove option from question
  const removeOption = (questionIndex, optionIndex) => {
    const updatedQuestions = [...questionnaire.questions];
    
    // Don't allow removing if there are only 2 options
    if (updatedQuestions[questionIndex].options.length <= 2) {
      return;
    }
    
    // If removing the correct option, make the first remaining option correct
    const isRemovingCorrect = updatedQuestions[questionIndex].options[optionIndex].isCorrect;
    
    updatedQuestions[questionIndex].options.splice(optionIndex, 1);
    
    if (isRemovingCorrect && updatedQuestions[questionIndex].options.length > 0) {
      updatedQuestions[questionIndex].options[0].isCorrect = true;
    }
    
    setQuestionnaire(prev => ({
      ...prev,
      questions: updatedQuestions
    }));
  };

  // Toggle active question
  const toggleQuestion = (index) => {
    setActiveQuestionIndex(activeQuestionIndex === index ? null : index);
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!questionnaire.title.trim()) {
      errors.title = 'Title is required';
    }
    
    if (questionnaire.questions.length === 0) {
      errors.questions = 'At least one question is required';
    }
    
    questionnaire.questions.forEach((question, qIndex) => {
      if (!question.text.trim()) {
        errors[`questions[${qIndex}].text`] = 'Question text is required';
      }
      
      if (question.options.length < 2) {
        errors[`questions[${qIndex}].options`] = 'At least two options are required';
      }
      
      let hasCorrectOption = false;
      
      question.options.forEach((option, oIndex) => {
        if (!option.text.trim()) {
          errors[`questions[${qIndex}].options[${oIndex}].text`] = 'Option text is required';
        }
        
        if (option.isCorrect) {
          hasCorrectOption = true;
        }
      });
      
      if (!hasCorrectOption) {
        errors[`questions[${qIndex}].correctOption`] = 'One option must be marked as correct';
      }
    });
    
    setValidationErrors(errors);
    
    // If there are errors, expand the question with errors
    if (Object.keys(errors).length > 0) {
      for (let i = 0; i < questionnaire.questions.length; i++) {
        const questionHasError = Object.keys(errors).some(key => 
          key.startsWith(`questions[${i}]`)
        );
        
        if (questionHasError) {
          setActiveQuestionIndex(i);
          break;
        }
      }
    }
    
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      window.scrollTo(0, 0);
      return;
    }
    
    try {
      setSaving(true);
      await api.put(`/questionnaires/${id}`, questionnaire);
      setSuccess(true);
      setError(null);
      
      // Scroll to top to show success message
      window.scrollTo(0, 0);
      
      // Navigate back to list after short delay
      setTimeout(() => {
        navigate('/questionnaires');
      }, 2000);
    } catch (err) {
      setError('Failed to update quiz. Please try again.');
      console.error('Error updating quiz:', err);
      window.scrollTo(0, 0);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading quiz...</p>
      </div>
    );
  }

  return (
    <div className="questionnaire-editor">
      <div className="editor-header">
        <button 
          className="back-button"
          onClick={() => navigate('/questionnaires')}
        >
          ← Back to Quizes
        </button>
        
        <h1>Edit Quiz</h1>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">
        <span className="success-icon">✓</span> Quiz updated successfully!
      </div>}
      
      {Object.keys(validationErrors).length > 0 && (
        <div className="error-message validation-errors">
          <h3>Please fix the following errors:</h3>
          <ul>
            {Object.values(validationErrors).map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}
      
      <form dir='rtl' onSubmit={handleSubmit}>
        <div className="editor-section">
          <div className="section-header">
            <h2>Basic Information</h2>
          </div>
          <div className="section-content">
            <div className="form-group">
              <label htmlFor="title">Title</label>
              <input
                type="text"
                id="title"
                name="title"
                value={questionnaire.title}
                onChange={handleChange}
                className={validationErrors.title ? 'input-error' : ''}
                placeholder="Enter Quiz title"
              />
              {validationErrors.title && (
                <div className="error-text">{validationErrors.title}</div>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Description (Optional)</label>
              <textarea
                id="description"
                name="description"
                rows="3"
                value={questionnaire.description || ''}
                onChange={handleChange}
                placeholder="Enter a description for this Quiz"
              ></textarea>
            </div>
          </div>
        </div>
        
        <div className="editor-section">
          <div className="section-header questions-header">
            <h2>Questions</h2>
            <button 
              type="button" 
              className="add-button"
              onClick={addQuestion}
            >
              + Add Question
            </button>
          </div>
          
          <div className="section-content">
            {validationErrors.questions && (
              <div className="error-message">{validationErrors.questions}</div>
            )}
            
            {questionnaire.questions.length === 0 ? (
              <div className="empty-questions">
                <div className="empty-icon">?</div>
                <p>No questions added yet.</p>
                <button 
                  type="button" 
                  className="add-button"
                  onClick={addQuestion}
                >
                  + Add Your First Question
                </button>
              </div>
            ) : (
              <div className="questions-list">
                {questionnaire.questions.map((question, questionIndex) => (
                  <div 
                    key={questionIndex} 
                    className={`question-card ${activeQuestionIndex === questionIndex ? 'active' : ''}`}
                    id={`question-${questionIndex}`}
                  >
                    <div 
                      className="question-header"
                      onClick={() => toggleQuestion(questionIndex)}
                    >
                      <div className="question-title">
                        <span className="question-number">{questionIndex + 1}</span>
                        <h3>{question.text || `Question ${questionIndex + 1}`}</h3>
                      </div>
                      <div className="question-actions">
                        <span className="options-count">
                          {question.options.length} options
                        </span>
                        <span className="expand-icon">
                          {activeQuestionIndex === questionIndex ? '▼' : '▶'}
                        </span>
                      </div>
                    </div>
                    
                    {activeQuestionIndex === questionIndex && (
                      <div className="question-body">
                        <div className="form-group">
                          <label>Question Text</label>
                          <input
                            type="text"
                            value={question.text}
                            onChange={(e) => handleQuestionChange(questionIndex, e)}
                            className={validationErrors[`questions[${questionIndex}].text`] ? 'input-error' : ''}
                            placeholder="Enter your question here"
                          />
                          {validationErrors[`questions[${questionIndex}].text`] && (
                            <div className="error-text">
                              {validationErrors[`questions[${questionIndex}].text`]}
                            </div>
                          )}
                        </div>
                        
                        <div className="form-group">
                          <div className="options-header">
                            <label>Options</label>
                            <button 
                              type="button"
                              className="add-option-button"
                              onClick={() => addOption(questionIndex)}
                            >
                              + Add Option
                            </button>
                          </div>
                          
                          {validationErrors[`questions[${questionIndex}].options`] && (
                            <div className="error-message">
                              {validationErrors[`questions[${questionIndex}].options`]}
                            </div>
                          )}
                          
                          {validationErrors[`questions[${questionIndex}].correctOption`] && (
                            <div className="error-message">
                              {validationErrors[`questions[${questionIndex}].correctOption`]}
                            </div>
                          )}
                          
                          <div className="options-list">
                            {question.options.map((option, optionIndex) => (
                              <div key={optionIndex} className="option-item">
                                <div className="option-content">
                                  <div className="radio-container">
                                    <input
                                      type="radio"
                                      name={`correctOption-${questionIndex}`}
                                      checked={option.isCorrect}
                                      onChange={() => handleCorrectOptionChange(questionIndex, optionIndex)}
                                      id={`option-${questionIndex}-${optionIndex}`}
                                    />
                                    <label 
                                      htmlFor={`option-${questionIndex}-${optionIndex}`}
                                      className="radio-label"
                                    >
                                      {option.isCorrect ? 'Correct' : ''}
                                    </label>
                                  </div>
                                  <div className="option-input-container">
                                    <input
                                      type="text"
                                      placeholder={`Option ${optionIndex + 1}`}
                                      value={option.text}
                                      onChange={(e) => handleOptionChange(questionIndex, optionIndex, e)}
                                      className={validationErrors[`questions[${questionIndex}].options[${optionIndex}].text`] ? 'input-error' : ''}
                                    />
                                    <button 
                                      type="button"
                                      className="remove-option-button"
                                      disabled={question.options.length <= 2}
                                      onClick={() => removeOption(questionIndex, optionIndex)}
                                      title={question.options.length <= 2 ? "At least two options are required" : "Remove this option"}
                                    >
                                      ×
                                    </button>
                                  </div>
                                </div>
                                {validationErrors[`questions[${questionIndex}].options[${optionIndex}].text`] && (
                                  <div className="error-text option-error">
                                    {validationErrors[`questions[${questionIndex}].options[${optionIndex}].text`]}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div className="question-footer">
                          <button 
                            type="button"
                            className="remove-question-button"
                            onClick={() => removeQuestion(questionIndex)}
                          >
                            Delete Question
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="form-actions">
          <button 
            type="button"
            className="cancel-button"
            onClick={() => navigate('/questionnaires')}
          >
            Cancel
          </button>
          <button 
            type="submit"
            className="save-button"
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner"></span>
                Saving...
              </>
            ) : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditQuestionnaire;